import React from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import { Money } from "../../components/ui";

/**
 * The pricing context above the target editor: what this reference trades at,
 * and what we last did with one.
 *
 * Fetched ONLY when a panel is open — `enabled` is the caller's `open` flag —
 * because six aggregates across 116 references on page load is the same N+1
 * that had this page rate-limiting itself. One request per reference you
 * actually look at, cached for the session.
 */
const fetchInsight = async (itemId) => {
  const { data } = await axios.get(`/api/core-list/${itemId}/insight`);
  return data;
};

const WINDOWS = [
  { key: "1m", label: "1M" },
  { key: "3m", label: "3M" },
  { key: "all", label: "All time" },
];

const MedianRow = ({ title, series }) => (
  <div className="buy-target-context__row">
    <span className="buy-target-context__row-label">{title}</span>
    {WINDOWS.map(({ key, label }) => {
      const cell = series?.[key];
      const points = Number(cell?.points) || 0;
      return (
        <span className="buy-target-context__cell" key={key}>
          <span className="buy-target-context__cell-label">{label}</span>
          {cell?.median ? (
            // Analytics money is in MINOR units, like the rest of PriceIQ.
            <Money value={cell.median} decimals={0} />
          ) : (
            <span className="buy-target-context__none">—</span>
          )}
          {/* A median of one price is not a market. Say how many it is made of
              rather than presenting one quote with the same confidence as
              forty. */}
          <span className="buy-target-context__points">
            {points === 1 ? "1 price" : `${points} prices`}
          </span>
        </span>
      );
    })}
  </div>
);

const BuyTargetContext = ({ itemId, open }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["core-list-insight", itemId],
    queryFn: () => fetchInsight(itemId),
    enabled: !!open && itemId != null,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (isLoading) {
    return <div className="buy-target-context is-loading">Loading pricing context…</div>;
  }

  // Non-fatal by design: the target editor below is the point of the panel and
  // must stay usable even when this context cannot be produced.
  if (isError || !data) {
    return (
      <div className="buy-target-context is-error">
        Couldn&apos;t load the pricing context for this reference.
      </div>
    );
  }

  const previous = data.previous || {};
  const hasPrevious = previous.listing_price != null || previous.buying_price != null;

  return (
    <div className="buy-target-context">
      <MedianRow title="Trade median" series={data.medians?.trade} />
      <MedianRow title="Market median" series={data.medians?.market} />

      <div className="buy-target-context__previous">
        <span className="buy-target-context__cell">
          <span className="buy-target-context__cell-label">Previous listing price</span>
          {previous.listing_price != null ? (
            <Money value={previous.listing_price} currency={previous.currency} decimals={0} />
          ) : (
            <span className="buy-target-context__none">—</span>
          )}
        </span>

        <span className="buy-target-context__cell">
          <span className="buy-target-context__cell-label">Previous buying price</span>
          {previous.buying_price != null ? (
            <Money value={previous.buying_price} currency={previous.currency} decimals={0} />
          ) : (
            <span className="buy-target-context__none">—</span>
          )}
        </span>

        {!hasPrevious && (
          <span className="buy-target-context__note">
            No completed order for this reference yet.
          </span>
        )}
      </div>
    </div>
  );
};

export default BuyTargetContext;
